package com.eladfinish.bible.contest.screens.settings.ui.screens

import androidx.compose.runtime.LaunchedEffect
import androidx.navigation.NavController
import androidx.navigation.NavGraphBuilder
import androidx.navigation.compose.navigation
import com.eladfinish.bible.contest.LocalAppActions
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.common.settings.KeyValueSettings
import com.eladfinish.bible.contest.common.settings.RusshwolfKeyValueSettings
import com.eladfinish.bible.contest.copied_to_clipboard
import com.eladfinish.bible.contest.di.config.DebugInfo
import com.eladfinish.bible.contest.di.getKoinInstance
import com.eladfinish.bible.contest.navigation.DebugFlagsScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.SettingsRootNavigationDestination
import com.eladfinish.bible.contest.navigation.SettingsScreenNavigationDestination
import com.eladfinish.bible.contest.navigation.composableAnimated
import com.eladfinish.bible.contest.navigation.popBackStackSafely
import com.eladfinish.bible.contest.preferences_have_been_reset
import com.eladfinish.bible.contest.screens.settings.debug.DebugFlagsScreen
import com.eladfinish.bible.contest.screens.settings.ui.components.ComponentNavigationDestination
import com.russhwolf.settings.ExperimentalSettingsApi
import com.russhwolf.settings.ObservableSettings
import com.russhwolf.settings.Settings
import com.russhwolf.settings.coroutines.getBooleanFlow
import dev.icerock.moko.resources.StringResource
import io.github.aakira.napier.Napier
import kotlinx.coroutines.flow.Flow

@OptIn(ExperimentalSettingsApi::class)
fun NavGraphBuilder.settingsGraph(
    navController: NavController,
    settings: Settings,
    observableSettings: ObservableSettings,
    versionInfo: String,
    currentShowToastStringResource: (StringResource) -> Unit,
    logEvent: (eventName: String, singleKey: String, singleValue: String) -> Unit,
) {
    navigation<SettingsRootNavigationDestination>(
        startDestination = SettingsScreenNavigationDestination,
    ) {
        settingsGraphInternal(
            navController = navController,
            settings = RusshwolfKeyValueSettings(settings),
            getBooleanFlow = { key, defaultValue ->
                observableSettings.getBooleanFlow(key, defaultValue)
            },
            versionInfo = versionInfo,
            rawSettings = settings,
            currentShowToastStringResource = currentShowToastStringResource,
            onVersionInfoClick = {
                Napier.d("onVersionInfoClick called - $it")
                currentShowToastStringResource(SharedRes.strings.copied_to_clipboard)
            },
            logEvent = logEvent,
        )
    }
}

private fun NavGraphBuilder.settingsGraphInternal(
    navController: NavController,
    settings: KeyValueSettings,
    rawSettings: Settings,
    versionInfo: String,
    currentShowToastStringResource: (StringResource) -> Unit,
    onVersionInfoClick: (String) -> Unit,
    logEvent: (eventName: String, singleKey: String, singleValue: String) -> Unit,
    getBooleanFlow: (key: String, defaultValue: Boolean) -> Flow<Boolean>,
) {

    composableAnimated<SettingsScreenNavigationDestination> {
        val appActions = LocalAppActions.current
        SettingsScreenRoot(
            settings = settings,
            onComponentClick = {
                navController.navigate(ComponentNavigationDestination(it))
            },
            versionInfo = versionInfo,
            resetPreferences = {
                rawSettings.clear()
                appActions.clearApplicationData()
                currentShowToastStringResource(SharedRes.strings.preferences_have_been_reset)
            },
            onVersionInfoClick = onVersionInfoClick,
            logEvent = logEvent,
            onDebugFlagsClick = {
                navController.navigate(DebugFlagsScreenNavigationDestination)
            },
        )
    }

    composableAnimated<DebugFlagsScreenNavigationDestination> {
        // Reachable by URL on web, so the screen itself is gated too, not just the row leading here.
        if (getKoinInstance<DebugInfo>().isDebugMode) {
            DebugFlagsScreen()
        } else {
            LaunchedEffect(Unit) {
                navController.popBackStackSafely()
            }
        }
    }

    composableAnimated<ComponentNavigationDestination>(
        typeMap = ComponentNavigationDestination.typeMap,
    ) { component ->
        SubSettingsScreens(
            component = component,
            navController = navController,
            getBooleanFlow = getBooleanFlow,
            settings = settings,
        )
    }
}
